import { AfterViewInit, Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Report } from 'src/app/models/report.model';
import { CommonService } from 'src/app/services/common.service';
import { ToastService } from 'src/app/services/toast.service';
import { ReportService } from 'src/app/services/report.service';
import { ReportFrequency } from 'src/app/enums/reportFrequency.enum';
import { ReportCategory } from 'src/app/enums/reportCategory.enum';
import { Router } from '@angular/router';
import { CoreGridComponent } from 'src/app/shared/grid/core-grid/core-grid.component';
import { AuthService } from 'src/app/services/auth.service';
import { DateFormatPipe } from 'src/app/shared/pipes/date-format.pipe';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { Observable, of } from 'rxjs';
import { GridViewService } from 'src/app/services/grid-view.service';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { ColumnConfig } from 'src/app/shared/grid/core-grid/columnConfig.model';
import { UserService } from 'src/app/services/user.service';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { TranslateService } from 'src/app/services/translate.service';

@Component({
  standalone: false,
  selector: 'app-report-list',
  templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
  styleUrls: ['./../../shared/grid/core-grid/core-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DateFormatPipe]
})
export class ReportListComponent extends CoreGridComponent<Report> implements OnInit, AfterViewInit {

  constructor(
    protected reportService: ReportService,
    protected toastService: ToastService,
    protected commonService: CommonService,
    protected dialog: MatDialog,
    protected authService: AuthService,
    private router: Router,
    private dateFormatPipe: DateFormatPipe,
    gridViewService: GridViewService,
    gridSettingsService: GridSettingsService,
    private userService: UserService,
    private organizationLevelService: OrganizationLevelService,
    private translateService: TranslateService
  ) {
    super(toastService, dialog, commonService, authService, gridViewService, gridSettingsService);
    this.gridService = reportService;
    this.addDisabled = true;
  }

  protected get objectId(): string {
    return RolePermissionObjects.Report;
  }

  protected override get isGridViewEnabled(): boolean { return true; }

  override initializeProperties(): void {
    this.addEntityTitle = 'Report';
    this.sortActive = 'createdDate';
    this.defaultOrderBy = 'desc';
    this.searchPlaceholder = 'Search Reports';
  }

  override initializeColumns(): void {
    this.columnConfigs = [
      new ColumnConfig<Report>({
        name: nameOf<Report>(x => x.name),
        displayName: 'Report Name',
        cellTemplate: (r) => r.name,
        onClick: (r) => this.goToReportView(r.id),
        textCssClass: () => 'btn-link cursor-pointer'
      }),
      new ColumnConfig<Report>({
        name: nameOf<Report>(x => x.category),
        displayName: 'Category',
        cellTemplate: (r) => this.getCategoryName(r.category),
        enumFilterMap: this.enumToValueNameArray(ReportCategory)
      }),
      new ColumnConfig<Report>({
        name: nameOf<Report>(x => x.type),
        displayName: 'Type',
        cellTemplate: (r) => r.type || '-'
      }),
      new ColumnConfig<Report>({
        name: nameOf<Report>(x => x.createdByUserId),
        displayName: 'Created By',
        cellTemplate: (r) => r.createdByUserEmail || '-'
      }),
      new ColumnConfig<Report>({
        name: nameOf<Report>(x => x.createdDate),
        displayName: 'Created Date',
        cellTemplate: (r) => this.dateFormatPipe.transform(r.createdDate)
      }),
      new ColumnConfig<Report>({
        name: nameOf<Report>(x => x.version),
        displayName: 'Version',
        cellTemplate: (r) => r.version || '-'
      }),
      new ColumnConfig<Report>({
        name: nameOf<Report>(x => x.frequency),
        displayName: 'Frequency',
        cellTemplate: (r) => this.getFrequencyName(r.frequency),
        enumFilterMap: this.enumToValueNameArray(ReportFrequency)
      }),
      new ColumnConfig<Report>({
        name: nameOf<Report>(x => x.organizationLevelId),
        displayName: 'Visibility',
        cellTemplate: (r) => r.organizationLevelName || '-'
      }),
      new ColumnConfig<Report>({
        name: nameOf<Report>(x => x.autoGenerated),
        displayName: 'Auto Generated',
        cellTemplate: (r) => this.translateService.getDynamicTranslation(r.autoGenerated ? 'Yes' : 'No')
      })
    ];
  }

  override objectFilterMap(propertyName: string): any | undefined {
    switch (propertyName) {
      case nameOf<Report>(x => x.createdByUserId): return this.userService;
      case nameOf<Report>(x => x.organizationLevelId): return this.organizationLevelService;
      default: return undefined;
    }
  }

  get hasOrgFilter() {
    return true;
  }

  get hasDateFilter() {
    return true;
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.loadHeaderData();
  }

  override initializeOverviewConfigs(): void {
    this.overviewConfigs = [];
  }

  override initializeTabs() {
    return [
      { routerLink: '/dashboard/reports', title: 'Reports' }
    ];
  }

  getCategoryName(category: ReportCategory): string {
    const name = this.commonService.getEnumName(ReportCategory, category);
    return this.translateService.getDynamicTranslation(name);
  }

  getFrequencyName(frequency: ReportFrequency): string {
    const name = this.commonService.getEnumName(ReportFrequency, frequency);
    return this.translateService.getDynamicTranslation(name);
  }

  goToReportView(reportId: string): void {
    this.router.navigate(['dashboard/report', reportId]);
  }

  override getOverviewData(): Observable<void> {
    return of(undefined);
  }

  override openEntityDialog(entityId?: string): void {
  }

  override openEntityDeleteDialog(entity: Report): void {
  }
}